/*
  Certified Softy sign-up drawer.

  The panel is in the page from the start so it can slide; this adds
  and removes `is-open`, and keeps it out of reach of the keyboard and
  screen readers while it is shut. Closes on the scrim, the close
  button and Escape, and gives focus back to whatever opened it.
*/
(function () {
  function init(scope) {
    var root = scope || document;
    var drawers = root.querySelectorAll('[data-sh-signup-drawer]');

    drawers.forEach(function (drawer) {
      if (drawer.dataset.shSignupReady) return;
      drawer.dataset.shSignupReady = 'true';

      var section = drawer.closest('.sh-section') || document;
      var opener = document.querySelector('[data-sh-signup-open][aria-controls="' + drawer.id + '"]');
      var panel = drawer.querySelector('.sh-stk__panel');

      /* Out to the end of <body>. Inside the section the scrim was
         boxed into an ancestor's stacking context and painted under
         the page; nothing encloses it here. Any copy left behind by
         an earlier render of this section goes first. */
      if (drawer.parentNode !== document.body) {
        var stale = document.querySelector('body > [data-sh-signup-drawer][id="' + drawer.id + '"]');
        if (stale && stale !== drawer) stale.remove();
        document.body.appendChild(drawer);
      }

      var SHOWN = 'sh-signup-shown';
      var JOINED = 'sh-signup-joined';

      /* Private windows and blocked site data make storage throw, so a
         failed read just means "not seen yet" and the drawer behaves
         as it would on a first visit. */
      function recalls(key) {
        try {
          var store = key === JOINED ? window.localStorage : window.sessionStorage;
          return store.getItem(key + ':' + drawer.id) === '1';
        } catch (e) {
          return false;
        }
      }

      function forget(key) {
        try {
          window.sessionStorage.removeItem(key + ':' + drawer.id);
        } catch (e) {
          /* Nothing to do. */
        }
      }

      function remember(key) {
        try {
          var store = key === JOINED ? window.localStorage : window.sessionStorage;
          store.setItem(key + ':' + drawer.id, '1');
        } catch (e) {
          /* Nothing to do: it opens again next visit at worst. */
        }
      }

      /* Opens itself the first time the shopper scrolls clear of the
         named section, once per visit and never after they join. */
      function autoOpen() {
        var selector = drawer.dataset.shSignupAuto;
        if (!selector || !('IntersectionObserver' in window)) return;
        if (recalls(SHOWN) || recalls(JOINED)) return;

        var after = document.querySelector(selector);
        if (!after) return;

        function arm() {
          var io = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              // Scrolled down past it: out of view and above the fold.
              if (entry.isIntersecting || entry.boundingClientRect.bottom > 0) return;
              io.disconnect();
              if (recalls(SHOWN) || recalls(JOINED)) return;
              remember(SHOWN);
              open(false);
            });
          });

          io.observe(after);
        }

        /* Watching starts at the top of the page. Landing part-way
           down - a reload, or a link to an anchor - waits for a real
           scroll first, so nobody is greeted by a modal on arrival. */
        if (window.scrollY > 0) {
          window.addEventListener('scroll', arm, { passive: true, once: true });
        } else {
          arm();
        }
      }

      function setShut(shut) {
        drawer.classList.toggle('is-open', !shut);
        drawer.inert = shut;
        if (opener) opener.setAttribute('aria-expanded', shut ? 'false' : 'true');
        // Same lock as the cart drawer (Dawn's class), so the html rule in
        // softy-shapes.css holds the page still behind either drawer.
        document.body.classList.toggle('overflow-hidden', !shut);
      }

      /* Whatever opened the drawer gets focus back when it shuts;
         an automatic open came from nowhere, so nothing does. */
      var openedBy = null;

      function open(takeFocus, source) {
        setShut(false);
        openedBy = takeFocus ? source || opener : null;
        if (!takeFocus) return;
        var first = drawer.querySelector('input:not([type="hidden"]), button');
        if (first) first.focus();
      }

      function close() {
        setShut(true);
        remember(SHOWN);
        /* Focus goes back without the page scrolling to the button. */
        if (openedBy) {
          try {
            openedBy.focus({ preventScroll: true });
          } catch (e) {
            openedBy.focus();
          }
        }
        openedBy = null;
      }

      /* A form that came back posted or with errors reopens itself,
         but only if it was this drawer that sent it: the inline field
         on the page shares the same customer form and shows its own
         result in place. */
      var SENT = 'sh-signup-sent';
      var form = drawer.querySelector('[data-sh-signup-form]');
      if (form) {
        form.addEventListener('submit', function () { remember(SENT); });
      }
      var posted = drawer.querySelector('[data-sh-signup-reopen]');
      if (posted && !recalls(SENT)) posted = null;
      forget(SENT);
      setShut(!posted);
      if (posted) {
        drawer.scrollIntoView({ block: 'nearest' });
        if (drawer.querySelector('[role="status"]')) remember(JOINED);
      }

      document.querySelectorAll('[data-sh-signup-open][aria-controls="' + drawer.id + '"]').forEach(function (el) {
        el.addEventListener('click', function (event) {
          event.preventDefault();
          open(true, el);
        });
      });

      /* Any link to the sign-up anchor - the nav item, the top bar -
         opens the drawer instead of scrolling to the spread. A link
         inside the header's menu shuts that menu first. Arriving from
         another page with the anchor in the address opens it too.
         A drawer with no spread of its own (the footer strip's, on
         every page but the homepage) names the anchor it stands in
         for, and then answers links to it whatever page they name. */
      var anchor = section.querySelector('.sh-stk__signup[id]');
      var standIn = drawer.dataset.shSignupHash || '';
      var hash = standIn || (anchor ? '#' + anchor.id : '');

      function pointsHere(link) {
        if (!hash || link.hash !== hash) return false;
        if (standIn) return true;
        return link.pathname === window.location.pathname || link.pathname === '';
      }

      if (hash) {
        document.addEventListener('click', function (event) {
          var link = event.target.closest('a[href*="' + hash + '"]');
          if (!link || !pointsHere(link)) return;
          event.preventDefault();
          var menu = link.closest('details[open]');
          if (menu) menu.removeAttribute('open');
          open(true, link);
        });

        /* The anchor comes off the address once it has done its job,
           so a reload or a shared link does not open the drawer again. */
        if (window.location.hash === hash) {
          open(true);
          if (window.history && window.history.replaceState) {
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
          }
        }
      }

      autoOpen();

      drawer.querySelectorAll('[data-sh-signup-close]').forEach(function (el) {
        el.addEventListener('click', close);
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && drawer.classList.contains('is-open')) close();
      });

      // Focus stays in the panel while it is open.
      drawer.addEventListener('keydown', function (event) {
        if (event.key !== 'Tab' || !panel) return;
        var focusable = panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
